/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Shared building blocks for the notification-engine detail (view) screens.
 *
 * The six masters differ in what they hold, not in how a detail screen frames
 * it: a hero that states identity, a stack of section cards, and one "Details"
 * card of the audit/record fields every master carries. Written once here so
 * the screens cannot drift apart the way six hand-built layouts would.
 */
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tag } from 'primereact/tag';
import { formatDate } from 'sharedUi/dateUtils';
import ActiveTag from '../common/active-tag';
import { Icon, type IconName } from '../common/icon';
import { projectsService } from '../../services/projects.service';
import { userLookupService } from '../../services/user-lookup.service';

export const CARD =
  'overflow-hidden rounded-xl border border-line bg-surface shadow-sm';
const CARD_HEAD =
  'flex items-center gap-2 border-b border-line px-4 py-3';

/** An all-zero id is the system principal, not a real user. */
const isSystemActor = (v: any): boolean => !v || /^0+$/.test(String(v));

export const BackLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button aria-label="Back"
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1.5 text-body-sm text-ink-muted transition-colors hover:text-ink"
  >
    <Icon name="arrow-left" size={14} /> {label}
  </button>
);

/** `color` is still accepted for callers but no longer used: the Azure look is
 *  monochrome — one uniform grey icon so headers read as a set, not a rainbow. */
export const CardHeader = (
  { icon, title, hint }: { icon: IconName; color?: string; title: string; hint?: ReactNode },
) => (
  <div className={CARD_HEAD}>
    <Icon name={icon} size={16} className="text-ink-muted" />
    <span className="font-heading text-heading font-semibold text-ink">{title}</span>
    {hint ? <span className="ml-auto text-caption text-ink-muted">{hint}</span> : null}
  </div>
);

/** One labelled value in a Details grid. */
export const Field = (
  { label, mono, children }: { label: string; mono?: boolean; children: ReactNode },
) => (
  <div>
    <div className="text-label font-medium text-ink-muted">{label}</div>
    <div className={`mt-0.5 ${mono
      ? 'font-mono text-body-sm text-ink-muted'
      : 'text-body text-ink'}`}>
      {children}
    </div>
  </div>
);

/** The blue "active" strip that sits at the foot of a hero. */
export const ActiveBanner = ({ text }: { text: string }) => (
  <div className="flex items-center gap-2 border-t border-blue-100 bg-blue-50/60 px-5 py-2.5 text-body-sm text-blue-800 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-200">
    <Icon name="info" size={15} />
    <span>{text}</span>
  </div>
);

/** An amber warning strip at the foot of a hero — for a state worth flagging. */
export const WarnBanner = ({ text }: { text: string }) => (
  <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-5 py-2.5 text-body-sm font-medium text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-200">
    <Icon name="alert-triangle" size={15} />
    <span>{text}</span>
  </div>
);

/** `chip` is still accepted for callers but no longer used: the Azure look uses
 *  one dark square with a white icon (like a project tile), not a per-type
 *  pastel. Reintroduce a single brand colour here if we ever want one. */
export const Hero = ({ icon, code, tags, description, actions, banner }: {
  icon: IconName;
  chip?: string;
  code: ReactNode;
  tags?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  banner?: ReactNode;
}) => (
  <div className={CARD}>
    <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3.5">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-white dark:bg-slate-700">
          <Icon name={icon} size={22} />
        </span>
        <div>
          <div className="mb-1 flex flex-wrap items-center gap-2.5">
            {/* Azure-style: a real name in the UI font, near-black and bold — not
                a monospace database token. Mono is reserved for true raw ids. */}
            <h1 className="text-2xl font-semibold tracking-tight text-ink">{code}</h1>
            {tags}
          </div>
          <p className="text-body-sm text-ink-muted">
            {description || <span className="italic text-ink-muted">No description</span>}
          </p>
        </div>
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
    </div>
    {banner}
  </div>
);

/** The "Details" card — the record fields every master carries, plus whatever
 *  entity-specific fields the caller supplies as `extra`.
 *
 *  The stored appId / createdBy / updatedBy are ids; a person recognises names,
 *  so both are resolved through their cached lookups and the id is only shown
 *  when nothing resolves it. */
export const RecordMeta = ({ data, extra }: { data: any; extra?: ReactNode }) => {
  const { data: projects } = useQuery({
    queryKey: ['accessibleProjects', 'engineView'],
    queryFn: () => projectsService.getAccessible(),
    staleTime: 3e5,
  });
  const { data: actorNames } = useQuery({
    queryKey: ['userNames', String(data.createdBy || ''), String(data.updatedBy || '')],
    queryFn: () => userLookupService.getNamesByIds([data.createdBy, data.updatedBy]),
    staleTime: 3e5,
  });

  const projectLabel = (appId: any): string => {
    if (!appId) return 'Global (all projects)';
    const p = (projects || []).find((x) => x._id === String(appId));
    if (!p) return String(appId);
    return p.appCode ? `${p.name} (${p.appCode})` : p.name;
  };
  const actorLabel = (v: any): string => {
    if (isSystemActor(v)) return 'System';
    return actorNames?.get(String(v)) || String(v);
  };

  return (
    <div className={CARD}>
      <CardHeader icon="info" title="Details" />
      <div className="grid grid-cols-1 gap-x-6 gap-y-3.5 px-4 py-4 sm:grid-cols-2">
        {extra}
        <Field label="Status"><ActiveTag row={data} /></Field>
        <Field label="Project">{projectLabel(data.appId)}</Field>
        <Field label="Created">
          {data.createdAt ? formatDate(data.createdAt) : '—'}
          <span className="ml-1 text-caption text-ink-muted">by {actorLabel(data.createdBy)}</span>
        </Field>
        <Field label="Updated">
          {data.updatedAt
            ? <>{formatDate(data.updatedAt)}<span className="ml-1 text-caption text-ink-muted">by {actorLabel(data.updatedBy)}</span></>
            : '—'}
        </Field>
        {data.isDelete && (
          <Field label="Deleted"><Tag value="Deleted" severity="danger" className="text-xs px-1.5 py-0.5" /></Field>
        )}
      </div>
    </div>
  );
};
